import { FC, useMemo } from 'react';
import { css } from '@emotion/react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { spacing, colors, fontStyles } from '../../../styles';
import { cleanMarkdownContent } from '../../../utils/contentCleaning';
import { ToolRendererProps } from './types';

// Extract content from a single catalyst result
const extractCatalystContent = (data: any): string => {
  if (typeof data === 'string') return data;
  return (
    data?.content ||
    data?.analysis ||
    data?.summary ||
    data?.output ||
    data?.catalysts ||
    (typeof data === 'object' && Object.keys(data).length === 1 ? Object.values(data)[0] : '') ||
    ''
  );
};

// Render markdown content with full styling
const MarkdownContent: FC<{ content: string }> = ({ content }) => (
  <div
    css={css`
      color: ${colors.liquorice};
      font-size: 14px;
      line-height: 1.4;

      html[data-theme='dark'] & {
        color: ${colors.sugar};
      }

      /* Markdown styles matching ChatPage */
      & p {
        margin: 0 0 ${spacing.centi} 0;
        &:last-child {
          margin-bottom: 0;
        }
      }
      & h1 {
        ${fontStyles.lion}
        margin: 0 0 ${spacing.centi} 0;
      }
      & h2 {
        ${fontStyles.dog}
        margin: 0 0 ${spacing.centi} 0;
      }
      & h3 {
        font-size: 16px;
        font-weight: 600;
        margin: 0 0 ${spacing.milli} 0;
      }
      & ul,
      & ol {
        margin: ${spacing.centi} 0;
        padding-left: ${spacing.regular};
      }
      & li {
        margin: ${spacing.milli} 0;
      }
      & code {
        background-color: rgba(0, 0, 0, 0.1);
        padding: ${spacing.micro} ${spacing.milli};
        border-radius: 4px;
      }
      & pre {
        background-color: rgba(0, 0, 0, 0.1);
        padding: ${spacing.milli};
        border-radius: 4px;
        overflow-x: auto;
        margin: ${spacing.milli} 0;
      }
      & strong {
        font-weight: normal;
      }
      & em {
        font-style: normal;
      }
      & a {
        color: ${colors.petrolBlue};
        html[data-theme='dark'] & {
          color: ${colors.skyBlue};
        }
      }

      /* Table styles matching ChatPage */
      & .table-wrapper {
        overflow-x: auto;
        -webkit-overflow-scrolling: touch;
        margin: ${spacing.deci} 0;
        border-radius: 8px;
        border: 1px solid ${colors.cloud};
      }

      & table {
        border-collapse: collapse;
        width: 100%;
        display: table;
        font-size: 0.9em;
        margin: 0;
      }

      & th,
      & td {
        border: none;
        border-bottom: 1px solid ${colors.cloud};
        padding: ${spacing.deci};
        text-align: left;
        vertical-align: top;
        white-space: normal;
        word-break: normal;
        line-height: 1.5;
      }

      & th {
        background-color: rgba(0, 0, 0, 0.05);
        font-weight: 600;
        min-width: 120px;
        white-space: nowrap;
        border-bottom: 2px solid ${colors.cloud};
      }

      & td {
        white-space: pre-line;

        & br {
          display: block;
          content: '';
          margin-top: 0.5em;
        }

        & ul,
        & ol {
          margin: 0.5em 0;
          padding-left: 1.5em;
        }

        & li {
          margin: 0.25em 0;
        }

        & strong {
          font-weight: normal;
        }

        & em {
          font-style: normal;
        }
      }

      & tr:nth-of-type(even) {
        background-color: rgba(0, 0, 0, 0.02);
      }

      & tr:last-child td {
        border-bottom: none;
      }

      html[data-theme='dark'] & {
        & .table-wrapper {
          border-color: ${colors.tungsten};
        }

        & th,
        & td {
          border-color: ${colors.tungsten};
        }

        & th {
          background-color: rgba(255, 255, 255, 0.05);
          border-bottom-color: ${colors.tungsten};
        }

        & tr:nth-of-type(even) {
          background-color: rgba(255, 255, 255, 0.02);
        }
      }
    `}
  >
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        table: ({ children, ...props }) => (
          <div className="table-wrapper">
            <table {...props}>{children}</table>
          </div>
        ),
        td: ({ children, ...props }) => <td {...props}>{children}</td>,
        th: ({ children, ...props }) => <th {...props}>{children}</th>,
        strong: ({ children }) => <span>{children}</span>,
        em: ({ children }) => <span>{children}</span>,
      }}
    >
      {cleanMarkdownContent(content)}
    </ReactMarkdown>
  </div>
);

// Render a single catalyst result
const SingleCatalystDisplay: FC<{ data: any; symbol?: string }> = ({ data, symbol }) => {
  const content = extractCatalystContent(data);

  // Handle error case
  if (data?.error) {
    return (
      <div
        css={css`
          font-size: 14px;
          color: ${colors.berry};
        `}
      >
        {symbol && <strong>{symbol}: </strong>}
        {data.error}
      </div>
    );
  }

  // If we have substantial content with markdown formatting, render it fully
  if (
    typeof content === 'string' &&
    content.length > 100 &&
    (content.includes('## ') || content.includes('### ') || content.includes('|'))
  ) {
    return (
      <div>
        {symbol && (
          <div
            css={css`
              font-weight: 600;
              font-size: 15px;
              margin-bottom: ${spacing.centi};
              color: ${colors.petrolBlue};
              html[data-theme='dark'] & {
                color: ${colors.skyBlue};
              }
            `}
          >
            {symbol}
          </div>
        )}
        <MarkdownContent content={content} />
      </div>
    );
  }

  // Fallback for short content or simple messages
  return (
    <div
      css={css`
        font-size: 14px;
        color: ${colors.silver};
      `}
    >
      {symbol && <strong>{symbol}: </strong>}
      {typeof content === 'string' && content.length > 0 ? content : 'Catalyst analysis available'}
    </div>
  );
};

export const CatalystRenderer: FC<ToolRendererProps> = ({ output, isLoading }) => {
  // Handle both string and object outputs with proper typing - must be before early returns
  const parsedOutput = useMemo((): any => {
    if (typeof output === 'string') {
      try {
        return JSON.parse(output);
      } catch {
        return null;
      }
    }
    return output;
  }, [output]);

  if (isLoading) {
    return (
      <div
        css={css`
          font-size: 14px;
          color: ${colors.silver};
        `}
      >
        Analyzing catalysts...
      </div>
    );
  }

  // Handle batch format: {"catalysts_analysis": [...], "metadata": {...}}
  if (parsedOutput && typeof parsedOutput === 'object' && Array.isArray(parsedOutput.catalysts_analysis)) {
    const results = parsedOutput.catalysts_analysis;
    return (
      <div
        css={css`
          display: flex;
          flex-direction: column;
          gap: ${spacing.deci};
        `}
      >
        <div
          css={css`
            font-size: 13px;
            color: ${colors.silver};
            margin-bottom: ${spacing.milli};
          `}
        >
          Analyzed {results.length} symbol{results.length !== 1 ? 's' : ''}
        </div>
        {results.map((item: any, index: number) => (
          <div
            key={item.symbol || index}
            css={css`
              ${index > 0 ? `border-top: 1px solid ${colors.cloud}; padding-top: ${spacing.deci};` : ''}
              html[data-theme='dark'] & {
                border-color: ${colors.tungsten};
              }
            `}
          >
            <SingleCatalystDisplay data={item.catalysts || item} symbol={item.symbol} />
          </div>
        ))}
      </div>
    );
  }

  // Extract content from various possible locations (legacy single-symbol format)
  // Use parsedOutput for structured data, but fall back to raw output if parsing failed
  const content = extractCatalystContent(parsedOutput || output);

  // If we have substantial content with markdown formatting, render it fully
  if (
    typeof content === 'string' &&
    content.length > 100 &&
    (content.includes('## ') || content.includes('### ') || content.includes('|'))
  ) {
    return <MarkdownContent content={content} />;
  }

  // Fallback for short content or simple messages
  return (
    <div
      css={css`
        font-size: 14px;
        color: ${colors.silver};
      `}
    >
      Catalyst analysis completed
    </div>
  );
};
